import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi, formatDateTime } from '../services/adminApi';
import type { PageResponse, RecentUserDto, RecentConversationDto, RecentFeedbackDto, SystemStatisticsDto, VersionInfoDto, SortParams, UserFilters, ConversationFilters, FeedbackFilters, ExternalIdStatisticsDto, ExternalIdSearchResultDto } from '../services/adminApi';
import EntityHover from '../components/EntityHover';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LogsTab } from './admin/LogsTab';
import { PropertiesTab } from './admin/PropertiesTab';
import { SqlConsoleTab } from './admin/SqlConsoleTab';
import { ExternalLinksTab } from './backoffice/tabs/ExternalLinksTab';
import { GITHUB_REPOSITORY_URL } from '../constants/config';
import './BackofficePage.css';

// Constants
const COMMIT_SHA_DISPLAY_LENGTH = 7;

type TabType = 'overview' | 'users' | 'conversations' | 'feedback' | 'metrics' | 'data-management' | 'logs' | 'properties' | 'sql-console' | 'external-links';

const BackofficePage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [statistics, setStatistics] = useState<SystemStatisticsDto | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Read tab from URL, default to 'overview'
    const activeTab = (searchParams.get('tab') as TabType) || 'overview';

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        try {
            const stats = await adminApi.getSystemStatistics();
            setStatistics(stats);
        } catch (err) {
            setError('Failed to load statistics');
            console.error(err);
        }
    };

    const navigateToTab = (tab: TabType, filters?: Record<string, string>) => {
        const params = new URLSearchParams();
        params.set('tab', tab);

        // Add any filter parameters
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    params.set(key, value);
                }
            });
        }

        setSearchParams(params);
    };

    return (
        <div className="backoffice-container">
            <div className="backoffice-tabs">
                <button
                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => navigateToTab('overview')}
                >
                    Overview
                </button>
                <button
                    className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => navigateToTab('users')}
                >
                    Users
                </button>
                <button
                    className={`tab-button ${activeTab === 'conversations' ? 'active' : ''}`}
                    onClick={() => navigateToTab('conversations')}
                >
                    Conversations
                </button>
                <button
                    className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
                    onClick={() => navigateToTab('feedback')}
                >
                    Feedback
                </button>
                <button
                    className={`tab-button ${activeTab === 'metrics' ? 'active' : ''}`}
                    onClick={() => navigateToTab('metrics')}
                >
                    Metrics
                </button>
                <button
                    className={`tab-button ${activeTab === 'data-management' ? 'active' : ''}`}
                    onClick={() => navigateToTab('data-management')}
                >
                    Data Management
                </button>
                <button
                    className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
                    onClick={() => navigateToTab('logs')}
                >
                    Logs
                </button>
                <button
                    className={`tab-button ${activeTab === 'properties' ? 'active' : ''}`}
                    onClick={() => navigateToTab('properties')}
                >
                    Properties
                </button>
                <button
                    className={`tab-button ${activeTab === 'sql-console' ? 'active' : ''}`}
                    onClick={() => navigateToTab('sql-console')}
                >
                    SQL Console
                </button>
                <button
                    className={`tab-button ${activeTab === 'external-links' ? 'active' : ''}`}
                    onClick={() => navigateToTab('external-links')}
                >
                    External Links
                </button>
            </div>

            <div className="backoffice-content">
                {error && <div className="error-message">{error}</div>}

                {activeTab === 'overview' && <OverviewTab statistics={statistics} navigateToTab={navigateToTab} />}
                {activeTab === 'users' && <UsersTab searchParams={searchParams} navigateToTab={navigateToTab} />}
                {activeTab === 'conversations' && <ConversationsTab searchParams={searchParams} navigateToTab={navigateToTab} />}
                {activeTab === 'feedback' && <FeedbackTab searchParams={searchParams} navigateToTab={navigateToTab} />}
                {activeTab === 'metrics' && <MetricsTab />}
                {activeTab === 'data-management' && <DataManagementTab />}
                {activeTab === 'logs' && <LogsTab />}
                {activeTab === 'properties' && <PropertiesTab />}
                {activeTab === 'sql-console' && <SqlConsoleTab />}
                {activeTab === 'external-links' && <ExternalLinksTab />}
            </div>
        </div>
    );
};

const OverviewTab: React.FC<{
    statistics: SystemStatisticsDto | null;
    navigateToTab: (tab: TabType, filters?: Record<string, string>) => void;
}> = ({ statistics, navigateToTab }) => {
    const [backendVersion, setBackendVersion] = useState<VersionInfoDto | null>(null);
    const [backendVersionError, setBackendVersionError] = useState<string | null>(null);

    // Frontend version is a constant, use useMemo instead of useState
    const frontendVersion = useMemo(() => ({
        version: __APP_VERSION__,
        commitSha: __APP_COMMIT_SHA__,
        // Backend generates the URL for us, but frontend needs to construct its own
        githubUrl: __APP_COMMIT_SHA__ !== 'unknown'
            ? `${GITHUB_REPOSITORY_URL}/commit/${__APP_COMMIT_SHA__}`
            : null
    }), []);

    useEffect(() => {
        let cancelled = false;

        const loadBackendVersion = async () => {
            try {
                const version = await adminApi.getBackendVersion();
                if (!cancelled) {
                    setBackendVersion(version);
                    setBackendVersionError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load backend version', err);
                    setBackendVersionError('Failed to load version');
                }
            }
        };
        loadBackendVersion();

        return () => {
            cancelled = true;
        };
    }, []);

    if (!statistics) {
        return <div className="loading">Loading statistics...</div>;
    }

    return (
        <div className="overview-tab">
            <div className="stats-grid">
                <div className="stat-card clickable" onClick={() => navigateToTab('users')}>
                    <h3>Total Users</h3>
                    <p className="stat-number">{statistics.totalUsers.toLocaleString()}</p>
                </div>
                <div className="stat-card clickable" onClick={() => navigateToTab('conversations')}>
                    <h3>Total Conversations</h3>
                    <p className="stat-number">{statistics.totalConversations.toLocaleString()}</p>
                </div>
                <div className="stat-card">
                    <h3>Total Messages</h3>
                    <p className="stat-number">{statistics.totalMessages.toLocaleString()}</p>
                </div>
                <div className="stat-card clickable" onClick={() => navigateToTab('users')}>
                    <h3>Active Users (24h)</h3>
                    <p className="stat-number">{statistics.activeUsersLast24Hours.toLocaleString()}</p>
                </div>
                <div className="stat-card clickable" onClick={() => navigateToTab('users')}>
                    <h3>Active Users (7d)</h3>
                    <p className="stat-number">{statistics.activeUsersLast7Days.toLocaleString()}</p>
                </div>
                <div className="stat-card clickable" onClick={() => navigateToTab('conversations')}>
                    <h3>Conversations (24h)</h3>
                    <p className="stat-number">{statistics.conversationsLast24Hours.toLocaleString()}</p>
                </div>
                <div className="stat-card clickable" onClick={() => navigateToTab('conversations')}>
                    <h3>Conversations (7d)</h3>
                    <p className="stat-number">{statistics.conversationsLast7Days.toLocaleString()}</p>
                </div>
                <div className="stat-card">
                    <h3>Frontend Version</h3>
                    <p className="stat-number version-number">{frontendVersion.version}</p>
                    {frontendVersion.githubUrl ? (
                        <a
                            href={frontendVersion.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="version-link"
                        >
                            {frontendVersion.commitSha.substring(0, COMMIT_SHA_DISPLAY_LENGTH)}
                        </a>
                    ) : (
                        <p className="version-sha">{frontendVersion.commitSha}</p>
                    )}
                </div>
                <div className="stat-card">
                    <h3>Backend Version</h3>
                    {backendVersion ? (
                        <>
                            <p className="stat-number version-number">{backendVersion.version}</p>
                            {backendVersion.githubUrl ? (
                                <a
                                    href={backendVersion.githubUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="version-link"
                                >
                                    {backendVersion.commitSha.substring(0, COMMIT_SHA_DISPLAY_LENGTH)}
                                </a>
                            ) : (
                                <p className="version-sha">{backendVersion.commitSha}</p>
                            )}
                        </>
                    ) : backendVersionError ? (
                        <p className="version-error">{backendVersionError}</p>
                    ) : (
                        <p className="version-sha">Loading...</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const UsersTab: React.FC<{
    searchParams: URLSearchParams;
    navigateToTab: (tab: TabType, filters?: Record<string, string>) => void;
}> = ({ searchParams, navigateToTab }) => {
    const [users, setUsers] = useState<PageResponse<RecentUserDto> | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [sort, setSort] = useState<SortParams>({ sortBy: 'lastLoginAt', sortDir: 'desc' });

    // Initialize filters from URL params
    const [filters, setFilters] = useState<UserFilters>({
        nameFilter: searchParams.get('nameFilter') || '',
        emailFilter: searchParams.get('emailFilter') || '',
        providerFilter: searchParams.get('providerFilter') || ''
    });

    const loadUsers = useCallback(async (page: number, sortParams: SortParams, filterParams: UserFilters) => {
        setLoading(true);
        try {
            const data = await adminApi.getRecentUsers(page, 20, sortParams, filterParams);
            setUsers(data);
            setCurrentPage(page);
        } catch (err) {
            console.error('Failed to load users', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers(0, sort, filters);
    }, [loadUsers, sort, filters]);

    const handlePrevPage = () => {
        if (users && users.hasPrevious) {
            loadUsers(currentPage - 1, sort, filters);
        }
    };

    const handleNextPage = () => {
        if (users && users.hasNext) {
            loadUsers(currentPage + 1, sort, filters);
        }
    };

    const handleSort = (column: string) => {
        setSort(prev => {
            if (prev.sortBy === column) {
                return { sortBy: column, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' };
            }
            return { sortBy: column, sortDir: 'asc' };
        });
    };

    const handleFilterChange = (key: keyof UserFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value || undefined }));
    };

    if (!users && !loading) return <div>Loading...</div>;

    const getSortIcon = (column: string) => {
        if (sort.sortBy !== column) return ' ⇅';
        return sort.sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <div className="users-tab" ref={containerRef}>
            <h2>Recent Active Users</h2>
            <div className="filter-bar">
                <input
                    type="text"
                    placeholder="Filter by name..."
                    value={filters.nameFilter || ''}
                    onChange={(e) => handleFilterChange('nameFilter', e.target.value)}
                    className="filter-input"
                />
                <input
                    type="text"
                    placeholder="Filter by email..."
                    value={filters.emailFilter || ''}
                    onChange={(e) => handleFilterChange('emailFilter', e.target.value)}
                    className="filter-input"
                />
                <input
                    type="text"
                    placeholder="Filter by provider..."
                    value={filters.providerFilter || ''}
                    onChange={(e) => handleFilterChange('providerFilter', e.target.value)}
                    className="filter-input"
                />
            </div>
            {users && (
                <>
                    <div className="table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort('name')}>
                                        Name{getSortIcon('name')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('email')}>
                                        Email{getSortIcon('email')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('provider')}>
                                        Provider{getSortIcon('provider')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('lastLoginAt')}>
                                        Last Login{getSortIcon('lastLoginAt')}
                                    </th>
                                    <th>Conversations</th>
                                    <th>Messages</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.content.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td>{user.authProvider}</td>
                                        <td>{formatDateTime(user.lastLogin)}</td>
                                        <td>
                                            <span
                                                className="clickable-link"
                                                onClick={() => navigateToTab('conversations', { userEmailFilter: user.email })}
                                                title="View conversations by this user"
                                            >
                                                {user.conversationCount}
                                            </span>
                                        </td>
                                        <td>{user.messageCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={!users.hasPrevious || loading}>
                            ←
                        </button>
                        <span>Page {users.page + 1} of {users.totalPages}</span>
                        <button onClick={handleNextPage} disabled={!users.hasNext || loading}>
                            →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const ConversationsTab: React.FC<{
    searchParams: URLSearchParams;
    navigateToTab: (tab: TabType, filters?: Record<string, string>) => void;
}> = ({ searchParams }) => {
    const [conversations, setConversations] = useState<PageResponse<RecentConversationDto> | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sort, setSort] = useState<SortParams>({ sortBy: 'updatedAt', sortDir: 'desc' });

    // Initialize filters from URL params
    const [filters, setFilters] = useState<ConversationFilters>({
        titleFilter: searchParams.get('titleFilter') || '',
        userNameFilter: searchParams.get('userNameFilter') || '',
        userEmailFilter: searchParams.get('userEmailFilter') || ''
    });

    const loadConversations = useCallback(async (page: number, sortParams: SortParams, filterParams: ConversationFilters) => {
        setLoading(true);
        try {
            const data = await adminApi.getRecentConversations(page, 20, sortParams, filterParams);
            setConversations(data);
            setCurrentPage(page);
        } catch (err) {
            console.error('Failed to load conversations', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConversations(0, sort, filters);
    }, [loadConversations, sort, filters]);

    const handlePrevPage = () => {
        if (conversations && conversations.hasPrevious) {
            loadConversations(currentPage - 1, sort, filters);
        }
    };

    const handleNextPage = () => {
        if (conversations && conversations.hasNext) {
            loadConversations(currentPage + 1, sort, filters);
        }
    };

    const handleSort = (column: string) => {
        setSort(prev => {
            if (prev.sortBy === column) {
                return { sortBy: column, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' };
            }
            return { sortBy: column, sortDir: 'asc' };
        });
    };

    const handleFilterChange = (key: keyof ConversationFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value || undefined }));
    };

    const getSortIcon = (column: string) => {
        if (sort.sortBy !== column) return ' ⇅';
        return sort.sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    if (!conversations && !loading) return <div>Loading...</div>;

    return (
        <div className="conversations-tab">
            <h2>Recent Conversations</h2>
            <div className="filter-bar">
                <input
                    type="text"
                    placeholder="Filter by title..."
                    value={filters.titleFilter || ''}
                    onChange={(e) => handleFilterChange('titleFilter', e.target.value)}
                    className="filter-input"
                />
                <input
                    type="text"
                    placeholder="Filter by user name..."
                    value={filters.userNameFilter || ''}
                    onChange={(e) => handleFilterChange('userNameFilter', e.target.value)}
                    className="filter-input"
                />
                <input
                    type="text"
                    placeholder="Filter by email..."
                    value={filters.userEmailFilter || ''}
                    onChange={(e) => handleFilterChange('userEmailFilter', e.target.value)}
                    className="filter-input"
                />
            </div>
            {conversations && (
                <>
                    <div className="table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort('title')}>
                                        Title{getSortIcon('title')}
                                    </th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th className="sortable" onClick={() => handleSort('messageCount')}>
                                        Messages{getSortIcon('messageCount')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('createdAt')}>
                                        Created{getSortIcon('createdAt')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('updatedAt')}>
                                        Updated{getSortIcon('updatedAt')}
                                    </th>
                                    <th>Link</th>
                                </tr>
                            </thead>
                            <tbody>
                                {conversations.content.map((conv) => (
                                    <tr key={conv.id}>
                                        <td>{conv.title}</td>
                                        <td>{conv.userName}</td>
                                        <td>{conv.userEmail}</td>
                                        <td>{conv.messageCount}</td>
                                        <td>{formatDateTime(conv.createdAt)}</td>
                                        <td>{formatDateTime(conv.updatedAt)}</td>
                                        <td>
                                            <a href={conv.conversationUrl} target="_blank" rel="noopener noreferrer">
                                                View
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={!conversations.hasPrevious || loading}>
                            ←
                        </button>
                        <span>Page {conversations.page + 1} of {conversations.totalPages}</span>
                        <button onClick={handleNextPage} disabled={!conversations.hasNext || loading}>
                            →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const FeedbackTab: React.FC<{
    searchParams: URLSearchParams;
    navigateToTab: (tab: TabType, filters?: Record<string, string>) => void;
}> = ({ searchParams }) => {
    const [feedback, setFeedback] = useState<PageResponse<RecentFeedbackDto> | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sort, setSort] = useState<SortParams>({ sortBy: 'createdAt', sortDir: 'desc' });

    // Initialize filters from URL params
    const [filters, setFilters] = useState<FeedbackFilters>({
        type: searchParams.get('type') || '',
        userNameFilter: searchParams.get('userNameFilter') || '',
        userEmailFilter: searchParams.get('userEmailFilter') || ''
    });

    const loadFeedback = useCallback(async (page: number, sortParams: SortParams, filterParams: FeedbackFilters) => {
        setLoading(true);
        try {
            const data = await adminApi.getRecentFeedback(page, 20, sortParams, filterParams);
            setFeedback(data);
            setCurrentPage(page);
        } catch (err) {
            console.error('Failed to load feedback', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFeedback(0, sort, filters);
    }, [loadFeedback, sort, filters]);

    const handlePrevPage = () => {
        if (feedback && feedback.hasPrevious) {
            loadFeedback(currentPage - 1, sort, filters);
        }
    };

    const handleNextPage = () => {
        if (feedback && feedback.hasNext) {
            loadFeedback(currentPage + 1, sort, filters);
        }
    };

    const handleSort = (column: string) => {
        setSort(prev => {
            if (prev.sortBy === column) {
                return { sortBy: column, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' };
            }
            return { sortBy: column, sortDir: 'asc' };
        });
    };

    const handleFilterChange = (key: keyof FeedbackFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value || undefined }));
    };

    const getSortIcon = (column: string) => {
        if (sort.sortBy !== column) return ' ⇅';
        return sort.sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    if (!feedback && !loading) return <div>Loading...</div>;

    return (
        <div className="feedback-tab">
            <h2>User Feedback</h2>
            <div className="filter-bar">
                <select
                    value={filters.type || ''}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="filter-input"
                >
                    <option value="">All Types</option>
                    <option value="POSITIVE">Positive</option>
                    <option value="NEGATIVE">Negative</option>
                </select>
                <input
                    type="text"
                    placeholder="Filter by user name..."
                    value={filters.userNameFilter || ''}
                    onChange={(e) => handleFilterChange('userNameFilter', e.target.value)}
                    className="filter-input"
                />
                <input
                    type="text"
                    placeholder="Filter by email..."
                    value={filters.userEmailFilter || ''}
                    onChange={(e) => handleFilterChange('userEmailFilter', e.target.value)}
                    className="filter-input"
                />
            </div>
            {feedback && (
                <>
                    <div className="table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort('feedbackType')}>
                                        Type{getSortIcon('feedbackType')}
                                    </th>
                                    <th>User</th>
                                    <th>Text</th>
                                    <th className="sortable" onClick={() => handleSort('createdAt')}>
                                        Created{getSortIcon('createdAt')}
                                    </th>
                                    <th>Link</th>
                                </tr>
                            </thead>
                            <tbody>
                                {feedback.content.map((fb) => (
                                    <tr key={fb.id}>
                                        <td className={`feedback-type ${fb.feedbackType.toLowerCase()}`}>
                                            {fb.feedbackType === 'POSITIVE' ? '👍' : '👎'}
                                        </td>
                                        <td>{fb.userName}<br/><small>{fb.userEmail}</small></td>
                                        <td className="feedback-text">{fb.feedbackText || '(no comment)'}</td>
                                        <td>{formatDateTime(fb.createdAt)}</td>
                                        <td>
                                            <a href={fb.conversationUrl} target="_blank" rel="noopener noreferrer">
                                                View
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={!feedback.hasPrevious || loading}>
                            ←
                        </button>
                        <span>Page {feedback.page + 1} of {feedback.totalPages}</span>
                        <button onClick={handleNextPage} disabled={!feedback.hasNext || loading}>
                            →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const MetricsTab: React.FC = () => {
    const [allMetrics, setAllMetrics] = useState<Record<string, { displayName: string; value: string; rawValue: number; unit: string }> | null>(null);
    const [loading, setLoading] = useState(false);
    const [filterKey, setFilterKey] = useState('');

    useEffect(() => {
        const loadMetrics = async () => {
            setLoading(true);
            try {
                const metrics = await adminApi.getAvailableMetrics();
                setAllMetrics(metrics);
            } catch (err) {
                console.error('Failed to load metrics', err);
            } finally {
                setLoading(false);
            }
        };

        loadMetrics();
    }, []);

    const filteredMetrics = allMetrics
        ? Object.entries(allMetrics).filter(([metricName, data]) => {
            const searchTerm = filterKey.toLowerCase();
            return metricName.toLowerCase().includes(searchTerm) ||
                   data.displayName.toLowerCase().includes(searchTerm);
        })
        : [];

    if (loading) return <div>Loading...</div>;

    return (
        <div className="metrics-tab">
            <h2>System Metrics</h2>

            <div className="filter-bar">
                <input
                    type="text"
                    placeholder="Filter metrics by name..."
                    value={filterKey}
                    onChange={(e) => setFilterKey(e.target.value)}
                    className="filter-input"
                />
                <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                    Showing {filteredMetrics.length} of {allMetrics ? Object.keys(allMetrics).length : 0} metrics
                </span>
            </div>

            {allMetrics && (
                <div className="metrics-container">
                    <div className="stats-grid">
                        {filteredMetrics.map(([metricName, data]) => (
                            <div key={metricName} className="stat-card">
                                <h3>{data.displayName}</h3>
                                <p className="stat-number">{data.value}</p>
                                {data.unit && <p className="stat-unit">{data.unit}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <p className="metrics-note">
                Note: For detailed metric visualization, see the Grafana dashboard or access /actuator/metrics endpoints.
            </p>
        </div>
    );
};

interface RowEdit {
    bricklinkId: string;
    bricklinkName: string;
    bricksetId: string;
    bricksetName: string;
    brickowlId: string;
    brickowlName: string;
}

const DataManagementTab: React.FC = () => {
    const [statistics, setStatistics] = useState<ExternalIdStatisticsDto | null>(null);
    const [searchResults, setSearchResults] = useState<PageResponse<ExternalIdSearchResultDto> | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedEntityType, setSelectedEntityType] = useState<string>('SET');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [unmappedService, setUnmappedService] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [edits, setEdits] = useState<Map<string, RowEdit>>(new Map());
    const [saving, setSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {}
    });

    useEffect(() => {
        loadStatistics();

        // Cleanup: ensure body scroll is restored when component unmounts
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const loadStatistics = async () => {
        try {
            const stats = await adminApi.getExternalIdStatistics();
            setStatistics(stats);
        } catch (err) {
            console.error('Failed to load external ID statistics', err);
        }
    };

    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim() && !unmappedService) {
            setSearchResults(null);
            return;
        }

        setLoading(true);
        try {
            const results = await adminApi.searchExternalIds(
                selectedEntityType,
                searchQuery || undefined,
                unmappedService || undefined,
                undefined, // notFoundService
                currentPage,
                100
            );
            setSearchResults(results);
            // Initialize edits for new results
            const initialEdits = new Map<string, RowEdit>();
            results.content.forEach(result => {
                const bricklinkMapping = result.mappings.find(m => m.serviceName === 'bricklink');
                const bricksetMapping = result.mappings.find(m => m.serviceName === 'brickset');
                const brickowlMapping = result.mappings.find(m => m.serviceName === 'brickowl');
                initialEdits.set(result.rebrickableId, {
                    bricklinkId: bricklinkMapping?.serviceId || '',
                    bricklinkName: bricklinkMapping?.serviceNameValue || '',
                    bricksetId: bricksetMapping?.serviceId || '',
                    bricksetName: bricksetMapping?.serviceNameValue || '',
                    brickowlId: brickowlMapping?.serviceId || '',
                    brickowlName: brickowlMapping?.serviceNameValue || ''
                });
            });
            setEdits(initialEdits);
        } catch (err) {
            console.error('Failed to search external IDs', err);
        } finally {
            setLoading(false);
        }
    }, [selectedEntityType, searchQuery, unmappedService, currentPage]);

    const handleUnmappedClick = (entityType: string, service: string) => {
        setSelectedEntityType(entityType);
        setUnmappedService(service);
        setSearchQuery('');
        setCurrentPage(0);
    };

    const handleEditChange = (rebrickableId: string, field: keyof RowEdit, value: string) => {
        const newEdits = new Map(edits);
        const currentEdit = newEdits.get(rebrickableId) || {
            bricklinkId: '',
            bricklinkName: '',
            bricksetId: '',
            bricksetName: '',
            brickowlId: '',
            brickowlName: ''
        };
        newEdits.set(rebrickableId, { ...currentEdit, [field]: value });
        setEdits(newEdits);
    };

    const hasChanges = () => {
        if (!searchResults) return false;

        return searchResults.content.some(result => {
            const edit = edits.get(result.rebrickableId);
            if (!edit) return false;

            const bricklinkMapping = result.mappings.find(m => m.serviceName === 'bricklink');
            const bricksetMapping = result.mappings.find(m => m.serviceName === 'brickset');
            const brickowlMapping = result.mappings.find(m => m.serviceName === 'brickowl');

            return (
                edit.bricklinkId !== (bricklinkMapping?.serviceId || '') ||
                edit.bricklinkName !== (bricklinkMapping?.serviceNameValue || '') ||
                edit.bricksetId !== (bricksetMapping?.serviceId || '') ||
                edit.bricksetName !== (bricksetMapping?.serviceNameValue || '') ||
                edit.brickowlId !== (brickowlMapping?.serviceId || '') ||
                edit.brickowlName !== (brickowlMapping?.serviceNameValue || '')
            );
        });
    };

    const handleSaveAll = async () => {
        if (!searchResults || !hasChanges()) return;

        setSaving(true);
        try {
            const updates: Array<{
                entityType: string;
                rebrickableId: string;
                serviceName: string;
                serviceId: string;
                serviceNameValue: string | null;
            }> = [];

            searchResults.content.forEach(result => {
                const edit = edits.get(result.rebrickableId);
                if (!edit) return;

                const bricklinkMapping = result.mappings.find(m => m.serviceName === 'bricklink');
                const bricksetMapping = result.mappings.find(m => m.serviceName === 'brickset');
                const brickowlMapping = result.mappings.find(m => m.serviceName === 'brickowl');

                // Add BrickLink update if changed and has ID
                if (edit.bricklinkId && (
                    edit.bricklinkId !== (bricklinkMapping?.serviceId || '') ||
                    edit.bricklinkName !== (bricklinkMapping?.serviceNameValue || '')
                )) {
                    updates.push({
                        entityType: selectedEntityType,
                        rebrickableId: result.rebrickableId,
                        serviceName: 'bricklink',
                        serviceId: edit.bricklinkId,
                        serviceNameValue: edit.bricklinkName || null
                    });
                }

                // Add Brickset update if changed and has ID
                if (edit.bricksetId && (
                    edit.bricksetId !== (bricksetMapping?.serviceId || '') ||
                    edit.bricksetName !== (bricksetMapping?.serviceNameValue || '')
                )) {
                    updates.push({
                        entityType: selectedEntityType,
                        rebrickableId: result.rebrickableId,
                        serviceName: 'brickset',
                        serviceId: edit.bricksetId,
                        serviceNameValue: edit.bricksetName || null
                    });
                }

                // Add BrickOwl update if changed and has ID
                if (edit.brickowlId && (
                    edit.brickowlId !== (brickowlMapping?.serviceId || '') ||
                    edit.brickowlName !== (brickowlMapping?.serviceNameValue || '')
                )) {
                    updates.push({
                        entityType: selectedEntityType,
                        rebrickableId: result.rebrickableId,
                        serviceName: 'brickowl',
                        serviceId: edit.brickowlId,
                        serviceNameValue: edit.brickowlName || null
                    });
                }
            });

            if (updates.length > 0) {
                const result = await adminApi.batchUpdateExternalIds({ updates });
                alert(`Saved ${result.successCount} mappings successfully${result.failureCount > 0 ? `, ${result.failureCount} failed` : ''}`);

                // Reload results and statistics
                await handleSearch();
                await loadStatistics();
            }
        } catch (err) {
            console.error('Failed to save changes', err);
            alert('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const handleNextPage = () => {
        if (searchResults && searchResults.hasNext) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (searchResults && searchResults.hasPrevious) {
            setCurrentPage(prev => prev - 1);
        }
    };

    useEffect(() => {
        if (searchQuery.trim() || unmappedService) {
            handleSearch();
        }
    }, [searchQuery, unmappedService, handleSearch]);

    return (
        <div className="data-management-tab">
            <h2>Data Management</h2>

            <div className="stats-section">
                <h3>Unmapped Entities Statistics</h3>
                {statistics && (
                    <div className="stats-grid">
                        {Object.entries(statistics.byType).map(([type, stats]) => (
                            <div key={type} className="stat-card">
                                <h3>{type}</h3>
                                <p className="stat-number">{stats.totalEntities.toLocaleString()} Total</p>
                                {Object.entries(stats.unmappedByService).map(([service, count]) => (
                                    <p key={service} className="stat-detail">
                                        {service}: <strong
                                            className={count > 0 ? 'clickable-link' : ''}
                                            onClick={() => count > 0 && handleUnmappedClick(type, service)}
                                            style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                                        >{count.toLocaleString()}</strong> unmapped
                                    </p>
                                ))}
                            </div>
                        ))}
                        <div className="stat-card">
                            <h3>Total Unmapped</h3>
                            <p className="stat-number">{statistics.totalUnmappedEntities.toLocaleString()}</p>
                        </div>
                    </div>
                )}
            </div>

            <div style={{ marginTop: '30px', marginBottom: '30px' }}>
                <h3>Populate External IDs</h3>
                <p style={{ marginBottom: '15px', color: '#666' }}>
                    Trigger background jobs to populate missing external IDs from BrickLink or Rebrickable APIs.
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => {
                            setConfirmDialog({
                                isOpen: true,
                                title: 'Populate from BrickLink',
                                message: 'Start BrickLink ID population job? This will run in the background and populate missing BrickLink IDs.',
                                onConfirm: async () => {
                                    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                                    try {
                                        const result = await adminApi.triggerExternalIdPopulation('bricklink');
                                        alert(`Success: ${result.message}`);
                                        await loadStatistics();
                                    } catch (err) {
                                        console.error('Failed to trigger BrickLink population', err);
                                        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                                        alert(`Failed to trigger BrickLink population job: ${errorMessage}`);
                                    }
                                }
                            });
                        }}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Populate from BrickLink
                    </button>
                    <button
                        onClick={() => {
                            setConfirmDialog({
                                isOpen: true,
                                title: 'Populate from Rebrickable',
                                message: 'Start Rebrickable ID population job? This will run in the background and populate missing external IDs (BrickLink, BrickOwl) from Rebrickable API.',
                                onConfirm: async () => {
                                    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                                    try {
                                        const result = await adminApi.triggerExternalIdPopulation('rebrickable');
                                        alert(`Success: ${result.message}`);
                                        await loadStatistics();
                                    } catch (err) {
                                        console.error('Failed to trigger Rebrickable population', err);
                                        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                                        alert(`Failed to trigger Rebrickable population job: ${errorMessage}`);
                                    }
                                }
                            });
                        }}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Populate from Rebrickable
                    </button>
                </div>
            </div>

            <div style={{ marginTop: '30px', marginBottom: '30px' }}>
                <h3>Rebrickable Data Sync</h3>
                <p style={{ marginBottom: '15px', color: '#666' }}>
                    Trigger background job to sync Rebrickable data. Downloads fresh CSV files and performs incremental loading of new entities, including updating current-year sets with zero parts.
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => {
                            setConfirmDialog({
                                isOpen: true,
                                title: 'Sync Rebrickable Data',
                                message: 'Start Rebrickable data synchronization job? This will download fresh CSV files and perform incremental loading of new entities, including updating current-year sets with zero parts.',
                                onConfirm: async () => {
                                    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                                    try {
                                        const result = await adminApi.triggerDataSync();
                                        alert(`Success: ${result.message}`);
                                    } catch (err) {
                                        console.error('Failed to trigger data sync', err);
                                        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                                        alert(`Failed to trigger data sync: ${errorMessage}`);
                                    }
                                }
                            });
                        }}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#FF6B35',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Sync Rebrickable Data
                    </button>
                </div>
            </div>

            <div className="search-section">
                <h3>Search Entities</h3>
                {unmappedService && (
                    <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                        Showing <strong>{selectedEntityType}</strong> entities unmapped in <strong>{unmappedService}</strong>
                        <button
                            onClick={() => {
                                setUnmappedService('');
                                setSearchResults(null);
                            }}
                            style={{ marginLeft: '10px', cursor: 'pointer' }}
                        >
                            Clear Filter
                        </button>
                    </div>
                )}
                <div className="filter-bar">
                    <select
                        value={selectedEntityType}
                        onChange={(e) => setSelectedEntityType(e.target.value)}
                        className="filter-input"
                    >
                        <option value="SET">Sets</option>
                        <option value="PART">Parts</option>
                        <option value="MINIFIG">Minifigs</option>
                        <option value="COLOR">Colors</option>
                        <option value="ELEMENT">Elements</option>
                    </select>
                    <input
                        type="text"
                        placeholder="Search by ID or name..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setUnmappedService('');
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        className="filter-input"
                        disabled={!!unmappedService}
                    />
                    <button onClick={handleSearch} disabled={loading || (!searchQuery.trim() && !unmappedService)}>
                        Search
                    </button>
                </div>

                {loading && (
                    <div className="loading">
                        <LoadingAnimation size={120} message="Loading entities..." />
                    </div>
                )}

                {!loading && searchResults && (
                    <>
                        {hasChanges() && (
                            <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>You have unsaved changes</span>
                                <button
                                    onClick={handleSaveAll}
                                    disabled={saving}
                                    style={{
                                        padding: '8px 20px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: saving ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save All Changes'}
                                </button>
                            </div>
                        )}
                        <div className="table-container">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Rebrickable ID</th>
                                        <th>Name</th>
                                        <th>BrickLink ID</th>
                                        <th>BrickLink Name</th>
                                        <th>Brickset ID</th>
                                        <th>Brickset Name</th>
                                        <th>BrickOwl ID</th>
                                        <th>BrickOwl Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResults.content.map((result) => {
                                        const edit = edits.get(result.rebrickableId) || {
                                            bricklinkId: '',
                                            bricklinkName: '',
                                            bricksetId: '',
                                            bricksetName: '',
                                            brickowlId: '',
                                            brickowlName: ''
                                        };
                                        // Only use EntityHover for entity types that have detail pages
                                        const supportsHover = ['SET', 'PART', 'MINIFIG', 'ELEMENT'].includes(selectedEntityType);

                                        return (
                                            <tr key={result.rebrickableId}>
                                                <td style={{ fontWeight: '500' }}>
                                                    {supportsHover ? (
                                                        <EntityHover
                                                            type={(selectedEntityType.toLowerCase() + 's') as 'sets' | 'parts' | 'minifigs' | 'elements'}
                                                            id={result.rebrickableId}
                                                        >
                                                            {result.rebrickableId}
                                                        </EntityHover>
                                                    ) : (
                                                        result.rebrickableId
                                                    )}
                                                </td>
                                                <td>{result.entityName}</td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={edit.bricklinkId}
                                                        onChange={(e) => handleEditChange(result.rebrickableId, 'bricklinkId', e.target.value)}
                                                        placeholder="BrickLink ID"
                                                        style={{ width: '100%', padding: '4px 8px', fontSize: '13px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={edit.bricklinkName}
                                                        onChange={(e) => handleEditChange(result.rebrickableId, 'bricklinkName', e.target.value)}
                                                        placeholder="BrickLink Name (optional)"
                                                        style={{ width: '100%', padding: '4px 8px', fontSize: '13px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={edit.bricksetId}
                                                        onChange={(e) => handleEditChange(result.rebrickableId, 'bricksetId', e.target.value)}
                                                        placeholder="Brickset ID"
                                                        style={{ width: '100%', padding: '4px 8px', fontSize: '13px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={edit.bricksetName}
                                                        onChange={(e) => handleEditChange(result.rebrickableId, 'bricksetName', e.target.value)}
                                                        placeholder="Brickset Name (optional)"
                                                        style={{ width: '100%', padding: '4px 8px', fontSize: '13px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={edit.brickowlId}
                                                        onChange={(e) => handleEditChange(result.rebrickableId, 'brickowlId', e.target.value)}
                                                        placeholder="BrickOwl ID"
                                                        style={{ width: '100%', padding: '4px 8px', fontSize: '13px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={edit.brickowlName}
                                                        onChange={(e) => handleEditChange(result.rebrickableId, 'brickowlName', e.target.value)}
                                                        placeholder="BrickOwl Name (optional)"
                                                        style={{ width: '100%', padding: '4px 8px', fontSize: '13px' }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="pagination">
                            <button onClick={handlePrevPage} disabled={!searchResults.hasPrevious || loading || hasChanges()}>
                                ←
                            </button>
                            <span>Page {searchResults.page + 1} of {searchResults.totalPages} ({searchResults.totalElements} total)</span>
                            <button onClick={handleNextPage} disabled={!searchResults.hasNext || loading || hasChanges()}>
                                →
                            </button>
                        </div>
                        {hasChanges() && (
                            <p style={{ textAlign: 'center', color: '#856404', marginTop: '10px', fontSize: '13px' }}>
                                Save your changes before navigating to another page
                            </p>
                        )}
                    </>
                )}
            </div>

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmLabel="Start Job"
                cancelLabel="Cancel"
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
};

export default BackofficePage;
